import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import debounce from "@/helpers/debounce";
import { applicationSearchKey } from "@/hooks/apis/queries/useSearchApplicationsInfiniteQuery/constants";

import { pageAndLimitDefaultValue } from "@/common/constants/common";
import { applicationsApiHandler } from "@/services/apiHandlers/applications";

const useSearchApplicationsInfiniteQuery = (initialValueSearch = "") => {
  const [searchTerm, setSearchTerm] = useState(initialValueSearch);

  const {
    data: searchedApplications,
    isLoading: isLoadingsFoundedApplications,
    hasNextPage: hasNextPageSearchedApplications,
    fetchNextPage: fetchNextPageSearchedApplications,
  } = useInfiniteQuery({
    queryKey: [applicationSearchKey, searchTerm],
    queryFn: ({ pageParam = 1 }) => {
      return applicationsApiHandler.searchApplication(searchTerm, {
        page: pageParam,
        limit: pageAndLimitDefaultValue.limit,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const {
        paginatorInfo: { hasMorePages, currentPage },
      } = lastPage;

      return hasMorePages ? currentPage + 1 : undefined;
    },
    enabled: Boolean(searchTerm),
  });

  const onSearchTermHandler = debounce((searchedVal: string) => {
    setSearchTerm(searchedVal);
  }, 500);

  const foundAppsAndTotalCount = useMemo(
    () => ({
      apps:
        searchedApplications?.pages.flatMap(
          (application) => application.items,
        ) || [],
      totalCount: searchedApplications?.pages[0].paginatorInfo.total || 0,
    }),
    [searchedApplications?.pages],
  );

  return {
    foundedApps: foundAppsAndTotalCount.apps,
    totalApplicationCount: foundAppsAndTotalCount.totalCount,
    fetchNextPageSearchedApplications,
    hasNextPageSearchedApplications,
    isLoadingsFoundedApplications,
    onSearchTermHandler,
    searchTerm,
  };
};

export default useSearchApplicationsInfiniteQuery;
